// Nebulae.tsx — Colourful additive gas clouds draped along the spiral arms, the
// other half (with GalacticCore) of the Stellaris-style backdrop. Deterministic:
// a fixed-seed RNG places clouds on the same two-arm log spiral generate.ts uses,
// so they hug the star arms. Flat on the galactic plane (XZ), depth-write off,
// drawn before the stars. Decorative only.
import { useMemo } from 'react';
import { AdditiveBlending, DoubleSide } from 'three';
import { makeRng } from '../galaxy/rng';
import { glowTexture } from './glow';

// Must track generate.ts so clouds land on the arms.
const ARMS = 2;
const TURNS = 2.4;
const ARM_SPREAD = 0.45;

const PALETTE = ['#3a2f8c', '#7a3a9e', '#2f6f8f', '#9e3a6b', '#3f4fa8', '#2f8f7a', '#8a4fb0'];

interface Cloud {
  pos: [number, number, number];
  scale: number;
  color: string;
  opacity: number;
  rot: number;
}

function buildClouds(): Cloud[] {
  const rng = makeRng(0x9e_b0_1a);
  const clouds: Cloud[] = [];
  const place = (large: boolean) => {
    const arm = clouds.length % ARMS;
    const t = Math.pow(rng.next(), 0.7);
    const angle = t * TURNS * Math.PI * 2 + (arm / ARMS) * Math.PI * 2;
    const dist = 6 + t * 48;
    const scatter = ARM_SPREAD * dist * (large ? 0.7 : 0.45);
    const x = Math.cos(angle) * dist + rng.range(-scatter, scatter);
    const z = Math.sin(angle) * dist + rng.range(-scatter, scatter);
    clouds.push({
      pos: [x, rng.range(-1.5, 1.5), z],
      // Broad faint regional washes vs tighter, denser defined clouds.
      scale: large
        ? rng.range(46, 86) * (0.7 + t * 0.6)
        : rng.range(16, 40) * (0.6 + t * 0.9),
      color: rng.pick(PALETTE),
      opacity: large ? rng.range(0.08, 0.16) : rng.range(0.22, 0.42),
      rot: rng.range(0, Math.PI * 2),
    });
  };
  for (let i = 0; i < 9; i++) place(true); // broad regional colour fields
  for (let i = 0; i < 28; i++) place(false); // defined clouds along the arms
  return clouds;
}

export function Nebulae() {
  const tex = useMemo(glowTexture, []);
  const clouds = useMemo(buildClouds, []);
  return (
    <group>
      {clouds.map((c, i) => (
        <mesh key={i} position={c.pos} rotation={[-Math.PI / 2, 0, c.rot]} renderOrder={-10}>
          <planeGeometry args={[c.scale, c.scale]} />
          <meshBasicMaterial
            map={tex}
            color={c.color}
            transparent
            opacity={c.opacity}
            blending={AdditiveBlending}
            depthWrite={false}
            depthTest={false}
            side={DoubleSide}
            toneMapped={false}
          />
        </mesh>
      ))}
    </group>
  );
}
